import {
	Button as RACButton,
	Tooltip,
	TooltipTrigger,
} from 'react-aria-components';

import { cn } from '@/utils/cn';

// An EMPTY slot in the dock's collection: a dashed, hollow ring the exact size of a filled
// orb. It must read as an absence — an outline with nothing inside — so the row says "there
// is room here" at a glance. A filled shape (even a desaturated one) reads as a THING, not
// as a vacancy, which is the whole point of the slot.
//
// The stroke is `--text-disabled` (#8f8f8f, 3.05:1 on the #f8f8f8 dock) rather than the 12%
// hairline tint (#dddddd, 1.28:1): this is an interactive control, not decorative trim, so it
// has to clear the 3:1 graphical-object floor. A `+` inside says "add", which is exactly right
// for a slot whose press starts a new strategy.
//
// Two shapes, because the two placements have different jobs:
//   `variant="invite"` — the owner has NO strategies yet, so this carries the ring AND its label
//     in one wide press target. It is NOT the dock's only control: the manage glyph renders
//     beside it in every state, including an empty dock.
//   `variant="trailing"` — the bare ring, in two places now: after the filled orbs while the
//     menu is open, and ALONE in a resting bar whose owner has strategies but runs none. It
//     carries no visible words, so it carries a tooltip instead.
export function DockSlot({
	variant,
	onPress,
}: {
	variant: 'invite' | 'trailing';
	onPress: () => void;
}) {
	const label =
		variant === 'invite' ? 'Create Strategy' : 'Add another strategy';

	const button = (
		<RACButton
			data-testid="strategy-dock-slot"
			data-slot-variant={variant}
			onPress={onPress}
			aria-label={label}
			className={cn(
				`group cursor-pointer text-(--text-secondary) transition-colors
				duration-150 outline-none hover:text-(--text-primary)
				focus-visible:text-(--text-primary) motion-reduce:transition-none`,
				variant === 'invite'
					? // The same wide target the old "No Strategy" invite had: ring + label in one
						// button, so a first-run owner gets a comfortably large thing to press. The
						// width buys comfort, not compliance: this bar's manage glyph is size-7 (28px)
						// and every orb is size-8 (32px), and both clear the 24px WCAG floor.
						//
						// The gap is tight because the ring's size-8 box already contributes ~6px of
						// slack on its right edge — the optical gap is that plus this one.
						'flex items-center gap-1 text-sm font-medium'
					: 'grid shrink-0 place-items-center',
			)}
		>
			{/* The empty slot itself. The OUTER box is size-8 to match a filled orb's button, so
			    the bar's height is identical with and without it (menuRectFrom measures that
			    height to position the menu, before the morph and again one frame into it). The
			    RING inside is --orb-size, the same box a StrategyOrb's canvas paints, so the
			    empty slot and a filled avatar read at exactly the same diameter. */}
			<span aria-hidden className="grid size-8 shrink-0 place-items-center">
				<span
					className={cn(
						`group-pressed:scale-[0.94] motion-reduce:group-pressed:scale-100
						grid size-[var(--orb-size,20px)] place-items-center rounded-full
						border border-dashed border-(--text-disabled) bg-transparent
						text-(--text-disabled) transition-[border-color,color,transform]
						duration-150 group-hover:border-(--text-secondary)
						group-hover:text-(--text-secondary) motion-reduce:transition-none`,
					)}
				>
					<PlusGlyph />
				</span>
			</span>
			{variant === 'invite' && <span>{label}</span>}
		</RACButton>
	);

	// The invite says its own name in visible text, so a tooltip would only repeat it. The
	// trailing ring carries no words, and it can now be the whole resting bar, so it gets the
	// same tooltip every orb in this bar already has.
	if (variant === 'invite') return button;

	return (
		<TooltipTrigger delay={300}>
			{button}
			<Tooltip
				placement="bottom"
				offset={6}
				className="z-50 max-w-xs rounded-md bg-gray-800 px-2.5 py-1.5 text-xs
					leading-snug text-white shadow-md"
			>
				{label}
			</Tooltip>
		</TooltipTrigger>
	);
}

// A hairline plus — the slot's "this is where the next one goes" mark. Deliberately thin and
// small so the dashed ring stays the dominant read.
function PlusGlyph() {
	return (
		<svg viewBox="0 0 16 16" width={9} height={9} aria-hidden fill="none">
			<path
				d="M8 3.5v9M3.5 8h9"
				stroke="currentColor"
				strokeWidth={1.75}
				strokeLinecap="round"
			/>
		</svg>
	);
}
